import { get, post, put } from '@/api/apiService.ts';
import type {
  CatalogEntry,
  CatalogKind,
  CatalogEditDraft,
  CatalogEditCreateRequest,
  CatalogEditUpdateTranslationRequest,
} from '@/types/catalog.ts';

const catalogService = {
  getAll(): Promise<CatalogEntry[]> {
    return get<CatalogEntry[]>('/api/catalog');
  },

  getByKind(kind: CatalogKind): Promise<CatalogEntry[]> {
    return get<CatalogEntry[]>(`/api/catalog/${kind}`);
  },

  getByKindAndSlug(kind: CatalogKind, slug: string): Promise<CatalogEntry> {
    return get<CatalogEntry>(`/api/catalog/${kind}/${slug}`);
  },

  getInactiveByKind(kind: CatalogKind): Promise<CatalogEntry[]> {
    return get<CatalogEntry[]>(`/api/catalog/${kind}/inactive`);
  },

  createDraft(kind: CatalogKind, body: CatalogEditCreateRequest): Promise<CatalogEditDraft> {
    return post<CatalogEditCreateRequest, CatalogEditDraft>(`/api/catalog/${kind}/edit`, body);
  },

  getDraft(draftId: string): Promise<CatalogEditDraft> {
    return get<CatalogEditDraft>(`/api/catalog/edits/${draftId}`);
  },

  getMyPendingDrafts(): Promise<CatalogEditDraft[]> {
    return get<CatalogEditDraft[]>('/api/catalog/edits/my-pending');
  },

  updateDraftTranslation(
    draftId: string,
    body: CatalogEditUpdateTranslationRequest,
  ): Promise<CatalogEditDraft> {
    return put<CatalogEditUpdateTranslationRequest, CatalogEditDraft>(
      `/api/catalog/edits/${draftId}/translation`,
      body,
    );
  },

  confirmDraft(draftId: string): Promise<CatalogEditDraft> {
    return post<Record<string, never>, CatalogEditDraft>(
      `/api/catalog/edits/${draftId}/confirm`,
      {},
    );
  },

  discardDraft(draftId: string): Promise<CatalogEditDraft> {
    return post<Record<string, never>, CatalogEditDraft>(
      `/api/catalog/edits/${draftId}/discard`,
      {},
    );
  },

  retryDraft(draftId: string): Promise<CatalogEditDraft> {
    return post<Record<string, never>, CatalogEditDraft>(
      `/api/catalog/edits/${draftId}/retry`,
      {},
    );
  },

  restoreEntry(id: string): Promise<CatalogEntry> {
    return post<Record<string, never>, CatalogEntry>(
      `/api/catalog/entries/${id}/restore`,
      {},
    );
  },
};

export default catalogService;
